import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, ChevronDown, CheckCircle2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

// Person header action for Top-Recruit sign-in access. Reuses the standard Invite User workflow and server permissions.
export function InviteToTopRecruit({ personId, onChanged }) {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get(`/persons/${personId}/top-recruit-access`).then((r) => setS(r.data)).catch(() => setS(null)); }, [personId]);
  useEffect(() => { load(); }, [load]);
  if (!s) return null;
  if (s.state === "active") return <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="top-recruit-user-badge"><CheckCircle2 className="w-3.5 h-3.5" />Top-Recruit User</span>;
  if (!s.can_invite || !["none", "pending", "expired"].includes(s.state)) return null;
  const p = s.prefill;
  const startInvite = () => {
    if (s.is_candidate) { setOpen(true); return; }
    navigate(`/admin/users?invite=1&person_id=${personId}&name=${encodeURIComponent(`${p.first_name || ""} ${p.last_name || ""}`.trim())}&email=${encodeURIComponent(p.email || "")}`);
  };
  const resend = async () => {
    try { const { data } = await api.post(`/persons/${personId}/invite-to-top-recruit`); toast.success(data.status === "resent" ? "Invitation Resent" : "Invitation Sent", { description: data.message }); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      {s.state === "none" && <Button className="bg-[#006090] hover:bg-[#004E75] text-white" onClick={startInvite} data-testid="invite-to-top-recruit"><UserPlus className="w-4 h-4 mr-1.5" />Invite to Top-Recruit</Button>}
      {s.state === "expired" && <Button className="bg-[#006090] hover:bg-[#004E75] text-white" onClick={s.invitation ? resend : startInvite} data-testid="send-new-invitation"><UserPlus className="w-4 h-4 mr-1.5" />Send New Invitation</Button>}
      {s.state === "pending" && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="outline" className="border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100" data-testid="invitation-pending">Invitation Pending<ChevronDown className="w-4 h-4 ml-1" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white">
            {s.invitation?.sent_at && <p className="px-2 py-1.5 text-xs text-slate-500">Sent {new Date(s.invitation.sent_at).toLocaleDateString()}{s.invitation.invited_by_name ? ` by ${s.invitation.invited_by_name}` : ""}</p>}
            <DropdownMenuItem onClick={resend} data-testid="resend-invitation">Resend Invitation</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>)}
      {open && <CandidateInviteDialog personId={personId} p={p} onClose={() => setOpen(false)} onSent={() => { setOpen(false); load(); onChanged?.(); }} />}
    </>
  );
}

function CandidateInviteDialog({ personId, p, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/persons/${personId}/invite-to-top-recruit/preview`).then((r) => setPreview(r.data)).catch(() => setPreview(false)); }, [personId]);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/persons/${personId}/invite-to-top-recruit`); toast.success("Invitation Sent", { description: `${p.preferred_name || p.first_name} will receive a secure link to create their own password.` }); onSent(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const row = (l, v, id) => <p className="text-sm" data-testid={id}><span className="text-slate-500">{l}: </span><b className="text-slate-800">{v || "—"}</b></p>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="invite-top-recruit-dialog">
        <DialogHeader><DialogTitle>Invite to Top-Recruit</DialogTitle></DialogHeader>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid sm:grid-cols-2 gap-1">
          {row("Name", `${p.first_name || ""} ${p.last_name || ""}`, "invite-prefill-name")}
          {row("Preferred name", p.preferred_name || p.first_name, "invite-prefill-preferred")}
          {row("Email", p.email, "invite-prefill-email")}
          {row("Group", p.organization_name, "invite-prefill-group")}
          {row("Classifications", (p.classifications || []).map((c) => c.replace(/_/g, " ")).join(", "), "invite-prefill-classifications")}
          {row("Access", "Candidate Portal", "invite-access-type")}
        </div>
        <p className="text-xs text-slate-500">They'll get a secure, expiring link and create their own password. No staff access is granted.</p>
        {preview && <div><p className="text-xs font-semibold text-slate-600 mb-1" data-testid="invite-preview-subject">Subject: {preview.subject}</p>
          <iframe title="Invitation preview" srcDoc={preview.html} sandbox="" className="w-full h-[45vh] rounded border border-slate-200 bg-white" data-testid="invite-preview-frame" /></div>}
        {preview === false && <p className="text-xs text-rose-600">Preview unavailable.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={busy} onClick={send} data-testid="invite-top-recruit-send">Send Invitation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
